/**
 * Feature Flag Service
 * POV-0002: Frontend flag gating for drawer vs legacy view
 */
import api from './api';

const FLAGS_CACHE_KEY = 'feature_flags';
const FLAGS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

class FeatureFlagService {
  constructor() {
    this.cache = {};
    this.cacheTimestamp = 0;
  }

  /**
   * Check if a specific feature flag is enabled
   */
  async isEnabled(flagKey) {
    try {
      const response = await api.get(`/api/v1/flags/check/${flagKey}`);
      return response.data.enabled;
    } catch (error) {
      console.warn(`Failed to check flag ${flagKey}:`, error);
      return false; // Default to disabled on error
    }
  }

  /**
   * Get all flags for current user context
   * Results are cached for performance
   */
  async getAllFlags(forceRefresh = false) {
    const now = Date.now();
    
    // Return cached if valid
    if (!forceRefresh && this.cacheTimestamp && (now - this.cacheTimestamp) < FLAGS_CACHE_TTL) {
      return this.cache;
    }

    try {
      const response = await api.get('/api/v1/flags/evaluate');
      this.cache = response.data.flags || {};
      this.cacheTimestamp = now;
      
      // Also store in localStorage for persistence
      localStorage.setItem(FLAGS_CACHE_KEY, JSON.stringify({
        flags: this.cache,
        timestamp: this.cacheTimestamp
      }));

      return this.cache;
    } catch (error) {
      console.warn('Failed to fetch feature flags:', error);
      
      // Try to use cached value from localStorage
      const stored = localStorage.getItem(FLAGS_CACHE_KEY);
      if (stored) {
        const { flags } = JSON.parse(stored);
        return flags;
      }
      
      return {};
    }
  }

  /**
   * Get flag value from cache (synchronous)
   * Use getAllFlags() first to populate cache
   */
  getFlagSync(flagKey) {
    // Try memory cache
    if (this.cache[flagKey] !== undefined) {
      return this.cache[flagKey];
    }

    // Try localStorage
    const stored = localStorage.getItem(FLAGS_CACHE_KEY);
    if (stored) {
      const { flags } = JSON.parse(stored);
      if (flags[flagKey] !== undefined) {
        return flags[flagKey];
      }
    }

    return false; // Default to disabled
  }

  /**
   * Clear flag cache (call after login/logout)
   */
  clearCache() {
    this.cache = {};
    this.cacheTimestamp = 0;
    localStorage.removeItem(FLAGS_CACHE_KEY);
  }

  /**
   * Check if Part Overview Drawer should be used
   * This is the main flag for the new UI
   */
  async usePartOverviewDrawer() {
    return await this.isEnabled('part_overview_drawer');
  }

  /**
   * Synchronous check for Part Overview Drawer
   */
  usePartOverviewDrawerSync() {
    return this.getFlagSync('part_overview_drawer');
  }
}

export const featureFlagService = new FeatureFlagService();
export default featureFlagService;
